import React, {useState, useEffect, createContext, useContext} from 'react';
import uuidv4 from 'uuid/v4';

/** @import { Logger, LoggerComponent, LoggerFactory, LogMessage } from "./LoggerTypes" */

const sendLogMessage = (
    /** @type {object} */ payload, 
    /** @type {string | null} */ authToken
) => {
    /** @type {Record<string, string>} */
    const headers = {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
    };

    return new Promise((resolve, _reject) => {
        fetch('/logs', {
            method: 'post',
            headers: headers,
            body: JSON.stringify([payload])
        }).then(() => {
            resolve(undefined);
        }).catch(err => {
            console.error('Problem sending logs to server', err);
            resolve(undefined); // swallow errors so logging can't break app
        })
    })
}

/** @type {LogMessage} */
export const logMessage = async (level, message, context, exception, authToken) => {
    if (typeof exception !== 'string') {
        exception = JSON.stringify(exception)
    }
    
    let payload = {
        id: uuidv4(),
        level: level,
        message: message,
        exception: exception || null,
        context: context && {
            sessionId: context.sessionId,
            durableSessionId: context.durableSessionId,
            customerId: context.customerId || null,
            correlationId: context.correlationId || null,
            pageName: context.pageName || null,
            userAction: context.userAction || null,
            additionalData: context.additionalData || {}
        }
    };

    await sendLogMessage(payload, authToken);
}

/** @type {LoggerFactory} */
function createLogger(
    sessionId, 
    authToken, 
    durableSessionId
) {
    return {
        error: (message, context, exception) => void logMessage('error', message, {sessionId, durableSessionId, ...context}, exception, authToken),
        warn: (message, context, exception) => void logMessage('warn', message, {sessionId, durableSessionId, ...context}, exception, authToken),
        info: (message, context, exception) => void logMessage('info', message, {sessionId, durableSessionId, ...context}, exception, authToken),
        debug: (message, context, exception) => void logMessage('debug', message, {sessionId, durableSessionId, ...context}, exception, authToken)
    }
}

export const LoggerContext = createContext(/** @type {Logger | null} */ (null));

export const withLogger = (/** @type {React.FC<{ logger: Logger | null; }>} */ Component) => {
    return (/** @type {object} */ props) => (
        <LoggerContext.Consumer>
            {context => <Component logger={context} {...props} />}
        </LoggerContext.Consumer>
    );
}

export const useLogger = () => {
    return /** @type {ReturnType<createLogger>} */ (useContext(LoggerContext));
}

// TODO: buffer logs to stop the chatter

export const getDurableSessionId = () => {
    if (!sessionStorage.getItem('lt-session')) {
        let ltSessionId = uuidv4();
        sessionStorage.setItem('lt-session', ltSessionId)
        return ltSessionId;
    } else {
        return sessionStorage.getItem('lt-session');
    }
}

/** @type {LoggerComponent} */
export default (props) => {
    let ltSession = getDurableSessionId();
    let [logger, setLogger] = useState(createLogger(props.sessionId, props.authToken, ltSession));
    
    useEffect(() => {
        setLogger(createLogger(props.sessionId, props.authToken, ltSession));
    }, [props.sessionId, props.authToken]);
    
    return (
        <LoggerContext.Provider value={logger}>
            {props.children}
        </LoggerContext.Provider>
    );
};